import React, { useEffect } from 'react';
import { activeUsersWebSocketService } from 'services/activeUsersWebSocketService';
import { calculationWebSocketService } from 'services/calculationWebSocketService';
import { useStore } from 'hooks/useStore';
import { toast } from 'react-toastify';

/**
 * Global WebSocket Initializer Component
 * 
 * This component initializes the active-users and calculation-update WebSocket connections
 * when the application loads. It ensures that these WebSocket services are connected 
 * globally and available throughout the application.
 * 
 * Features:
 * - Connects to active-users WebSocket for real-time user tracking
 * - Connects to calculation-update WebSocket for real-time calculation progress tracking
 * - Handles connection status and reconnection
 * - Provides global WebSocket state management
 * - Ensures single connections per socket type
 * - Note: job-updates WebSocket is handled by JobPaste component
 */
const GlobalWebSocketInitializer: React.FC = () => {
    const { UserStore } = useStore();
    const environment = import.meta.env.VITE_ENVIRONMENT;

    useEffect(() => {
        // Set UserStore instances for WebSocket services
        activeUsersWebSocketService.setUserStore(UserStore);
        calculationWebSocketService.setUserStore(UserStore);

        // Initialize active-users WebSocket
        try {
            activeUsersWebSocketService.connect();
        } catch (error) {
            if (environment === 'development') {
                console.error('❌ Failed to initialize Active Users WebSocket:', error);
            }
            toast.error('Failed to initialize Active Users WebSocket');
        }

        // Initialize calculation-update WebSocket
        try {
            calculationWebSocketService.initialize();
        } catch (error) {
            if (environment === 'development') {
                console.error('❌ Failed to initialize Calculation WebSocket:', error);
            }
            toast.error('Failed to initialize Calculation WebSocket');
        }

        // Cleanup function
        return () => {
            activeUsersWebSocketService.disconnect();
            calculationWebSocketService.cleanup();
        };
    }, [UserStore]); // Add UserStore to dependency array

    return null; // This component doesn't render anything
};

export default GlobalWebSocketInitializer;
